import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

/** Marker stored in sessionStorage to detect an SSO redirect already in flight.
 *  Prevents an infinite loop if the IdP redirect fails to complete OR returns
 *  the operator to an unauthenticated state on the next mount. */
const AUTO_REDIRECT_KEY = 'messaging-front:auto-sso-redirect';

/**
 * "Wait for auth + tenant data" gate for the operator dashboard, so the SSO +
 * tenant-loading UX is consistent across the app.
 *
 * **Auto-SSO on unauth:** if the operator isn't authenticated, this gate
 * triggers `login()` immediately (no button click required). Two safeguards:
 *
 *   1. **One redirect per session.** We set a sessionStorage marker before
 *      calling login(). If we land back here still-unauthenticated AND the
 *      marker is set, we don't retry — we render the manual "Sign in"
 *      button as a fallback. Avoids infinite loop when SSO is misconfigured
 *      or the user denies consent.
 *   2. **Manual button as fallback.** Always available; covers the
 *      misconfiguration case + lets the user re-trigger if needed.
 */
export function AppGate({ children }: Props) {
  const { authenticated, login } = useAuth();
  const { loading, error, refetch } = useTenant();
  const { t } = useTranslation();
  const [autoRedirectAttempted, setAutoRedirectAttempted] = useState(() => {
    try {
      return sessionStorage.getItem(AUTO_REDIRECT_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Auto-trigger SSO redirect on first unauthenticated render.
  useEffect(() => {
    if (authenticated) {
      // Clean up the marker once we're successfully authenticated so a
      // future logout + return cleanly auto-redirects again.
      try {
        sessionStorage.removeItem(AUTO_REDIRECT_KEY);
      } catch {
        // ignore
      }
      return;
    }
    if (autoRedirectAttempted) return;
    try {
      sessionStorage.setItem(AUTO_REDIRECT_KEY, '1');
    } catch {
      // ignore — auto-retry won't be loop-protected but that's edge-case
    }
    setAutoRedirectAttempted(true);
    void login();
  }, [authenticated, autoRedirectAttempted, login]);

  if (!authenticated) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-surface-2 overflow-hidden p-4">
        <div className="text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-3 sm:mb-4" />
          <p className="text-sm sm:text-base text-muted px-4 mb-4">
            {autoRedirectAttempted ? t('gate.redirecting') : t('gate.connecting')}
          </p>
          {/* Fallback manual button — only meaningful after auto-redirect ran
              and we still haven't gotten authenticated. Always renders so
              keyboard users can trigger it explicitly. */}
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(AUTO_REDIRECT_KEY);
              } catch {
                // ignore
              }
              void login();
            }}
            className="text-sm text-primary hover:text-primary underline"
          >
            {t('gate.manualLogin')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-surface-2 overflow-hidden p-4">
        <div className="text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto mb-3 sm:mb-4" />
          <p className="text-sm sm:text-base text-muted px-4">
            {t('gate.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-surface-2 overflow-hidden p-4">
        <div className="text-center max-w-md w-full px-4">
          <div className="mb-4 sm:mb-6">
            <svg
              className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-danger"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink mb-2">
            {t('gate.loadFailed')}
          </h1>
          <p className="text-sm sm:text-base text-muted mb-2 sm:mb-3 break-words">
            {error}
          </p>
          <p className="text-xs sm:text-sm text-muted mb-4 sm:mb-5">
            {t('gate.loadFailedHelp')}
          </p>
          <button
            type="button"
            onClick={refetch}
            className="inline-flex items-center justify-center bg-primary hover:opacity-90 text-on-primary text-sm sm:text-base font-medium px-5 py-2 rounded"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
